import type {
  BatchCreateLocationsBody,
  SlaPolicy,
  ChangeUserRoleBody,
  CreateAssetBody,
  CreateLocationBody,
  EditLocationBody,
  CreatePpmScheduleBody,
  CreatePrivilegedUserBody,
  DeactivateUserBody,
  EntityStatus,
  ExportTicketsQuery,
  ListAuditQuery,
  CreateJobBody,
  EditJobBody,
  JobEditView,
  OverrideJobStatusBody,
  SetLocationStatusBody,
  SuperAdminDeleteBody,
  SystemAuditEntry,
  TempPasswordResponse,
  UpdateLocationBody,
  UpdateUserBody,
  UpsertChecklistTemplateBody,
} from '@nbh/shared';
import { DEVICE_ID_HEADER } from '@nbh/shared';
import { getDeviceId } from './device';

/**
 * One fetch for every management call.
 *
 * The prefix is a parameter rather than a constant because the root tier sits on its
 * own path — see `superRequest`.
 */
async function requestAt<T>(base: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${base}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      [DEVICE_ID_HEADER]: getDeviceId(),
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...((init?.headers as Record<string, string> | undefined) ?? {}),
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      message?: string;
    } | null;
    // `message` first: Nest puts the useful sentence there and a generic label
    // ("Conflict", "Bad Request") in `error`, so reading `error` first showed the
    // operator the HTTP status word instead of the reason the act was refused. Zod
    // validation failures carry no `message`, so `error` still covers them.
    throw new Error(body?.message ?? body?.error ?? `Request failed (${response.status})`);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

function request<T>(path: string, init?: RequestInit): Promise<T> {
  return requestAt<T>('/api/admin', path, init);
}

/**
 * Root tier (decision D23), on `/api/super-admin` rather than under `/api/admin`.
 *
 * A separate prefix, not a separate client: the guard on these routes is a different
 * one, and sharing a prefix would make an ordinary admin's 403 read like a bug rather
 * than the boundary doing its job.
 */
function superRequest<T>(path: string, init?: RequestInit): Promise<T> {
  return requestAt<T>('/api/super-admin', path, init);
}

/** A downloaded attachment, held in memory until the caller writes it to disk. */
export interface FileDownload {
  blob: Blob;
  filename: string;
}

/**
 * Fetches a binary attachment.
 *
 * The session lives in an httpOnly cookie, so the download has to travel through
 * `fetch` with `credentials: 'include'` like every other call — a plain link or
 * `window.open` would arrive without the device header and, on some Android
 * webviews, without the cookie either, and come back a 401 the user cannot see.
 */
async function requestFile(path: string, fallbackFilename: string): Promise<FileDownload> {
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { [DEVICE_ID_HEADER]: getDeviceId() },
  });

  if (!response.ok) {
    // The failure body is JSON even though the success body is not.
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      message?: string;
    } | null;
    // `message` first: Nest puts the useful sentence there and a generic label
    // ("Conflict", "Bad Request") in `error`, so reading `error` first showed the
    // operator the HTTP status word instead of the reason the act was refused. Zod
    // validation failures carry no `message`, so `error` still covers them.
    throw new Error(body?.message ?? body?.error ?? `Request failed (${response.status})`);
  }

  const suggested = filenameFromDisposition(response.headers.get('content-disposition'));
  return { blob: await response.blob(), filename: suggested ?? fallbackFilename };
}

/** Reads the server's suggested filename, preferring the RFC 5987 encoded form. */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) return decodeURIComponent(encoded[1]);

  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

export interface LocationNode {
  id: string;
  name: string;
  floor: string | null;
  qrCode: string | null;
  department: string | null;
  isActive: boolean;
  /**
   * Lifecycle state (D25), richer than `isActive` beside it: MAINTENANCE is neither
   * usable nor retired. Optional because the tree is served by the ordinary admin
   * endpoint, which predates the column — see `locationStatus`.
   */
  status?: EntityStatus;
  retiredAt?: string | null;
  children: LocationNode[];
}

/**
 * The lifecycle state to display for a place.
 *
 * Falls back to the `isActive` boolean when the API has not sent `status`. A room
 * that renders as RETIRED because a field was missing is a wrong label; a room that
 * renders as ACTIVE because a field was missing is a wrong label *and* an action the
 * operator should not have been offered, so the fallback follows the older flag.
 */
export function locationStatus(node: LocationNode): EntityStatus {
  return node.status ?? (node.isActive ? 'ACTIVE' : 'RETIRED');
}

export interface AdminAsset {
  id: string;
  name: string;
  category: string;
  model: string | null;
  manufacturer: string | null;
  serialNumber: string | null;
  qrCode: string | null;
  isActive: boolean;
  retiredAt: string | null;
  retiredReason: string | null;
  warrantyExpiresAt: string | null;
  locationId: string | null;
  location: { id: string; name: string } | null;
}

export interface AdminPpmSchedule {
  id: string;
  title: string;
  description: string | null;
  department: string;
  priority: string;
  intervalType: string;
  intervalValue: number;
  nextDueDate: string;
  isActive: boolean;
  retiredAt: string | null;
  retiredReason: string | null;
  locationId: string;
  assetId: string | null;
  checklistTemplateId: string;
  location: { id: string; name: string } | null;
  asset: { id: string; name: string } | null;
  checklistTemplate: { id: string; title: string } | null;
}

export interface ChecklistTemplateOption {
  id: string;
  title: string;
  department: string;
  _count: { items: number };
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  department: string;
  isActive: boolean;
  /** When access was switched off. Null while the account is still active. */
  retiredAt?: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AdminChecklistTemplate {
  id: string;
  title: string;
  description: string | null;
  department: string;
  isActive: boolean;
  items: Array<{
    id: string;
    stepNumber: number;
    instruction: string;
    isRequired: boolean;
    requirePhoto: boolean;
  }>;
  _count: { schedules: number };
}

/** The policy in force and where it came from — 'default' means the compiled fallback. */
export interface SlaPolicyView {
  policy: SlaPolicy;
  source: 'stored' | 'default';
  /** The reporting baseline riding the same settings row; null = full history. */
  analyticsBaselineAt: string | null;
}

/** The reporting baseline alone, as its own GET/PUT reads and writes it. */
export interface AnalyticsBaselineView {
  analyticsBaselineAt: string | null;
}

export const adminApi = {
  checklists: {
    list: () => request<AdminChecklistTemplate[]>('/checklists'),
    create: (body: UpsertChecklistTemplateBody) =>
      request<AdminChecklistTemplate>('/checklists', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: string, body: UpsertChecklistTemplateBody) =>
      request<AdminChecklistTemplate>(`/checklists/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    retire: (id: string) => request<unknown>(`/checklists/${id}`, { method: 'DELETE' }),
  },

  locations: {
    tree: () => request<LocationNode[]>('/locations'),
    create: (body: CreateLocationBody) =>
      request<{ id: string }>('/locations', { method: 'POST', body: JSON.stringify(body) }),
    batch: (body: BatchCreateLocationsBody) =>
      request<{ created: number; requested: number }>('/locations/batch', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: string, body: UpdateLocationBody) =>
      request<unknown>(`/locations/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    retire: (id: string) => request<unknown>(`/locations/${id}`, { method: 'DELETE' }),
  },

  // Assets and schedules are create-and-retire: there is deliberately no update call
  // for either. Replacing one means retiring it and creating its successor.
  assets: {
    list: (search?: string, includeRetired = false) => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (includeRetired) params.set('includeRetired', 'true');
      const query = params.toString();
      return request<AdminAsset[]>(`/assets${query ? `?${query}` : ''}`);
    },
    create: (body: CreateAssetBody) =>
      request<{ id: string }>('/assets', { method: 'POST', body: JSON.stringify(body) }),
    retire: (id: string, retiredReason: string) =>
      request<unknown>(`/assets/${id}/retire`, {
        method: 'PATCH',
        body: JSON.stringify({ retiredReason }),
      }),
  },

  ppm: {
    list: () => request<AdminPpmSchedule[]>('/ppm-schedules'),
    templates: () => request<ChecklistTemplateOption[]>('/ppm-schedules/checklist-templates'),
    create: (body: CreatePpmScheduleBody) =>
      request<{ id: string }>('/ppm-schedules', { method: 'POST', body: JSON.stringify(body) }),
    retire: (id: string, retiredReason: string) =>
      request<unknown>(`/ppm-schedules/${id}/retire`, {
        method: 'PATCH',
        body: JSON.stringify({ retiredReason }),
      }),
  },

  // Reporting is not under /api/admin: the export engine is its own module, and the
  // route is role-checked server-side exactly like the admin ones.
  reports: {
    /**
     * Ticket export for an inclusive `from`–`to` calendar range in property time.
     * Resolves with the bytes; writing them to disk is the caller's job.
     */
    exportTickets: (query: ExportTicketsQuery): Promise<FileDownload> => {
      const params = new URLSearchParams({
        format: query.format,
        from: query.from,
        to: query.to,
      });
      if (query.department) params.set('department', query.department);
      if (query.status) params.set('status', query.status);

      return requestFile(
        `/reports/tickets/export?${params.toString()}`,
        `tickets-${query.from}_${query.to}.${query.format}`,
      );
    },
  },

  /**
   * Staff accounts. Root-tier since D44, path unchanged — the client calls these URLs and
   * the move was about who may call them.
   *
   * There is no `create` here any more. It posted to `/admin/users`, a second creation
   * route on the same tier as `superAdmin.users.create` below and the one that wrote no
   * audit row; the route is deleted and both forms now call the one that does.
   */
  users: {
    list: () => request<AdminUser[]>('/users'),
    update: (id: string, body: UpdateUserBody) =>
      request<unknown>(`/users/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    resetPassword: (id: string) =>
      request<TempPasswordResponse>(`/users/${id}/reset-password`, { method: 'POST' }),
  },

  /**
   * Root tier (decision D23). Almost nothing here deletes: accounts deactivate,
   * places retire, and both keep their rows — which is why every call carries a
   * reason and lands in `SystemAuditLog`.
   *
   * The two `remove` calls are the exception (D25) and the only irreversible acts in
   * the app. They take a typed `confirm` alongside the reason, checked server-side
   * against the target's own email or name.
   */
  superAdmin: {
    /** The property's SLA windows (D8, Q-CONFIG-NOW). Root-only, audited server-side. */
    slaPolicy: {
      get: () => superRequest<SlaPolicyView>('/sla-policy'),
      set: (body: SlaPolicy) =>
        superRequest<SlaPolicyView>('/sla-policy', { method: 'PUT', body: JSON.stringify(body) }),
    },

    /**
     * The reporting baseline: the date the dashboard's figures count from. A lens,
     * not a rewrite — the server changes no ticket row, and null restores the full
     * history. Root-only, audited server-side with old and new values.
     */
    analyticsBaseline: {
      get: () => superRequest<AnalyticsBaselineView>('/analytics-baseline'),
      set: (analyticsBaselineAt: string | null) =>
        superRequest<AnalyticsBaselineView>('/analytics-baseline', {
          method: 'PUT',
          body: JSON.stringify({ analyticsBaselineAt }),
        }),
    },

    users: {
      list: () => superRequest<AdminUser[]>('/users'),
      /** The only route that may mint an ADMIN or another SUPER_ADMIN. */
      create: (body: CreatePrivilegedUserBody) =>
        superRequest<TempPasswordResponse>('/users', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      deactivate: (id: string, body: DeactivateUserBody) =>
        superRequest<unknown>(`/users/${id}/deactivate`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      // Restoring access takes no reason: it is the reversal of a deactivation that
      // already carries one, and it is logged either way.
      reactivate: (id: string) =>
        superRequest<unknown>(`/users/${id}/reactivate`, { method: 'PATCH' }),
      changeRole: (id: string, body: ChangeUserRoleBody) =>
        superRequest<unknown>(`/users/${id}/role`, { method: 'PATCH', body: JSON.stringify(body) }),
      /** Irreversible (D25). `confirm` must equal the account's own email. */
      remove: (id: string, body: SuperAdminDeleteBody) =>
        superRequest<unknown>(`/users/${id}`, { method: 'DELETE', body: JSON.stringify(body) }),
    },

    locations: {
      create: (body: CreateLocationBody) =>
        superRequest<{ id: string }>('/locations', { method: 'POST', body: JSON.stringify(body) }),
      /**
       * Corrects a place in situ. No reason field: this is the reversible, corrective
       * act — the one you reach for instead of deleting a typo that has rooms under it.
       */
      edit: (id: string, body: EditLocationBody) =>
        superRequest<unknown>(`/locations/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      /** `reason`, not `retiredReason`: the root tier names it the same way everywhere. */
      retire: (id: string, reason: string) =>
        superRequest<unknown>(`/locations/${id}/retire`, {
          method: 'PATCH',
          body: JSON.stringify({ reason }),
        }),
      // Un-retiring takes no reason, matching user reactivation: it reverses an act
      // that already carries one, and it is logged either way.
      reinstate: (id: string) =>
        superRequest<unknown>(`/locations/${id}/reinstate`, {
          method: 'PATCH',
          body: JSON.stringify({}),
        }),
      /** Moves between ACTIVE and MAINTENANCE. Retiring keeps its own route. */
      setStatus: (id: string, body: SetLocationStatusBody) =>
        superRequest<unknown>(`/locations/${id}/status`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      /** Irreversible (D25). `confirm` must equal the place's own name. */
      remove: (id: string, body: SuperAdminDeleteBody) =>
        superRequest<unknown>(`/locations/${id}`, { method: 'DELETE', body: JSON.stringify(body) }),
    },

    /**
     * Job governance (D27). A job is a ticket — the queue staff work from is labelled
     * Jobs — so these write the same rows the technicians' queue reads.
     */
    jobs: {
      /**
       * The editable view: ids rather than names, so the form's selects can be
       * prefilled. `api.getTicket` is the technicians' read and returns neither.
       */
      get: (id: string) => superRequest<JobEditView>(`/jobs/${id}`),
      create: (body: CreateJobBody) =>
        superRequest<{ id: string; reference: number }>('/jobs', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      /** Corrective and reversible, so no reason is required — matching location edit. */
      edit: (id: string, body: EditJobBody) =>
        superRequest<unknown>(`/jobs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      /**
       * Forces a state the lifecycle refuses, which is why this one *does* demand a
       * reason. Cancelling is this route with `status: 'CANCELLED'` — the soft
       * alternative to deleting, keeping the row, its history and its chat.
       */
      setStatus: (id: string, body: OverrideJobStatusBody) =>
        superRequest<unknown>(`/jobs/${id}/status`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      /** Irreversible. `confirm` must equal the job's reference number or its title. */
      remove: (id: string, body: SuperAdminDeleteBody) =>
        superRequest<unknown>(`/jobs/${id}`, { method: 'DELETE', body: JSON.stringify(body) }),
    },

    /** Newest first, capped server-side — an unbounded page would crawl over 3G. */
    audit: (query: Partial<ListAuditQuery> = {}) => {
      const params = new URLSearchParams();
      if (query.action) params.set('action', query.action);
      if (query.actorId) params.set('actorId', query.actorId);
      if (query.limit != null) params.set('limit', String(query.limit));
      const encoded = params.toString();
      return superRequest<SystemAuditEntry[]>(`/audit${encoded ? `?${encoded}` : ''}`);
    },
  },
};
